import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { repairService } from '../../services/repair.service';
import { RepairStatusBadge } from '../../components/repairs/RepairStatusBadge';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/Button';
import { Loader2, Search, Filter, Smartphone, ChevronRight, Calendar, Clock } from 'lucide-react';

export const RepairList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');

  const { data: repairs, isLoading } = useQuery({
    queryKey: ['repairs'],
    queryFn: repairService.getMyRepairs,
  });

  const filteredRepairs = repairs?.filter((repair) => {
    const matchesSearch = repair.repairNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (repair.deviceCategoryId && repair.deviceCategoryId.toString().includes(searchTerm));
    const matchesFilter = filterStatus === 'ALL' || repair.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const statusOptions = [
    { value: 'ALL', label: 'All Repairs' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'WAITING_PARTS', label: 'Waiting for Parts' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CLOSED', label: 'Closed' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-[rgb(var(--foreground))]">My Repairs</h1>
        <Link to="/dashboard/repairs/new">
          <Button variant="primary">
            <Smartphone className="mr-2 h-4 w-4" />
            Book New Repair
          </Button>
        </Link>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--light-secondary))]" />
          <input
            type="text"
            placeholder="Search repairs by number or device..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--background))] pl-10 pr-4 py-2 text-[rgb(var(--foreground))] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--light-secondary))]" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full sm:w-48 rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--background))] pl-10 pr-4 py-2 text-[rgb(var(--foreground))] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary appearance-none"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Repairs List */}
      {filteredRepairs && filteredRepairs.length > 0 ? (
        <div className="space-y-4">
          {filteredRepairs.map((repair) => (
            <Link key={repair.id} to={`/dashboard/repairs/${repair.id}`}>
              <Card className="transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <span className="font-mono text-sm font-semibold text-primary">
                          #{repair.repairNumber}
                        </span>
                        <RepairStatusBadge status={repair.status} size="sm" />
                      </div>
                      <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">
                        {/* Add device name from joined data */}Device
                      </p>
                      {repair.faults && Array.isArray(repair.faults) && repair.faults.length > 0 && (
                        <p className="mt-1 text-xs text-[rgb(var(--light-secondary))]">
                          Faults: {repair.faults.slice(0, 3).join(', ')}
                          {repair.faults.length > 3 && ` +${repair.faults.length - 3} more`}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <div className="flex items-center space-x-2 text-sm text-[rgb(var(--light-secondary))]">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>{new Date(repair.createdAt).toLocaleDateString()}</span>
                      </div>
                      {repair.estimatedCompletion && (
                        <div className="flex items-center space-x-2 text-sm text-[rgb(var(--light-secondary))]">
                          <Clock className="h-3.5 w-3.5" />
                          <span>Est. {new Date(repair.estimatedCompletion).toLocaleDateString()}</span>
                        </div>
                      )}
                      <div className="mt-1 flex items-center text-primary">
                        <span className="text-sm font-medium">View Details</span>
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Smartphone className="mx-auto h-12 w-12 text-[rgb(var(--light-secondary))]" />
            <h3 className="mt-4 text-lg font-semibold text-[rgb(var(--foreground))]">No repairs found</h3>
            <p className="text-[rgb(var(--light-secondary))]">
              {searchTerm || filterStatus !== 'ALL'
                ? 'Try adjusting your search or filters'
                : 'Book your first repair today'}
            </p>
            {(searchTerm || filterStatus !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('ALL');
                }}
                className="mt-4 text-sm text-primary hover:underline"
              >
                Clear filters
              </button>
            )}
            {!searchTerm && filterStatus === 'ALL' && (
              <Link to="/dashboard/repairs/new">
                <Button variant="primary" className="mt-4">
                  <Smartphone className="mr-2 h-4 w-4" />
                  Book a Repair
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};